import React, { useCallback, useEffect, useRef, useState } from "react";
import Header from "../../components/Header";
import api from "../../services/api";
import { showError, showSuccess } from "../../services/alert";
import "./ImportacaoDados.css";

const statusLabel = {
  RECEBIDA: "Na fila",
  PROCESSANDO: "Processando",
  EXECUTANDO: "Realizando lançamentos",
  AGUARDANDO_CONFIRMACAO: "Aguardando confirmação",
  CONCLUIDA: "Concluída",
  CONCLUIDA_COM_PENDENCIAS: "Concluída com pendências",
  FALHOU: "Falhou",
  APROVADO: "Confirmado",
  PENDENTE: "Pendente",
  REJEITADO: "Rejeitado",
  EXECUTADO: "Executado",
  IGNORADO: "Ignorado",
  DUPLICADO: "Duplicado",
  ERRO: "Erro",
};

const formatDate = (value) =>
  value ? new Date(value).toLocaleString("pt-BR") : "—";
const ITENS_POR_PAGINA = 50;

export default function ImportacaoDados() {
  const inputRef = useRef(null);
  const [jobs, setJobs] = useState([]);
  const [metricas, setMetricas] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const [filtro, setFiltro] = useState("TODOS");
  const [pagina, setPagina] = useState(1);
  const [selecionados, setSelecionados] = useState(new Set());
  const [resolvendoLote, setResolvendoLote] = useState(false);
  const [corrigindo, setCorrigindo] = useState(null);
  const [salvandoCorrecao, setSalvandoCorrecao] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const [lista, resumo] = await Promise.all([
        api.get("/importacoes-dados"),
        api.get("/importacoes-dados/metricas"),
      ]);
      setJobs(lista.data || []);
      setMetricas(resumo.data || null);
      if (detalhe?.importacao?.id) {
        const atual = await api.get(
          `/importacoes-dados/${detalhe.importacao.id}`,
        );
        setDetalhe(atual.data);
      }
    } catch {
      /* atualização silenciosa */
    }
  }, [detalhe?.importacao?.id]);

  useEffect(() => {
    carregar();
    const timer = setInterval(carregar, 5000);
    return () => clearInterval(timer);
  }, [carregar]);

  const enviar = async (files) => {
    const selecionados = Array.from(files || []);
    if (!selecionados.length) return;
    const invalidos = selecionados.filter((file) => !/\.(csv|xlsx)$/i.test(file.name));
    if (invalidos.length) {
      showError(`Arquivos inválidos: ${invalidos.map((file) => file.name).join(", ")}. Envie somente CSV ou XLSX.`);
      return;
    }
    try {
      setEnviando(true);
      let enviados = 0;
      const falhas = [];
      for (const file of selecionados) {
        const form = new FormData();
        form.append("arquivo", file);
        try {
          await api.post("/importacoes-dados", form, { headers: { "Content-Type": "multipart/form-data" } });
          enviados++;
        } catch (error) {
          falhas.push(error.response?.data?.message ? `${file.name}: ${error.response.data.message}` : file.name);
        }
      }
      if (enviados) showSuccess(`${enviados} arquivo(s) recebido(s). O processamento continuará em segundo plano.`);
      if (falhas.length) showError(`Não foi possível enviar: ${falhas.join("; ")}`);
      await carregar();
    } finally {
      setEnviando(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const abrir = async (id) => {
    try {
      const { data } = await api.get(`/importacoes-dados/${id}`);
      setDetalhe(data);
      setFiltro("TODOS");
      setPagina(1);
      return data;
    } catch {
      showError("Não foi possível carregar a análise.");
      return null;
    }
  };

  const resolver = async (registro, acao) => {
    try {
      await api.post(
        `/importacoes-dados/${detalhe.importacao.id}/registros/${registro.id}/resolver`,
        { acao },
      );
      const atualizado = await abrir(detalhe.importacao.id);
      await carregar();
      if (atualizado?.importacao?.totalPendentes === 0)
        showSuccess("Interpretação confirmada. Confira abaixo o resultado dos lançamentos.");
    } catch (error) {
      showError(
        error.response?.data?.message ||
          "Não foi possível resolver o registro.",
      );
    }
  };

  const resolverLote = async (acao) => {
    if (!selecionados.size) return;
    try {
      setResolvendoLote(true);
      await api.post(`/importacoes-dados/${detalhe.importacao.id}/registros/resolver-lote`, {
        registroIds: Array.from(selecionados),
        acao,
      });
      setSelecionados(new Set());
      const atualizado = await abrir(detalhe.importacao.id);
      await carregar();
      if (atualizado?.importacao?.totalPendentes === 0)
        showSuccess("Interpretações confirmadas. A execução dos lançamentos foi iniciada.");
    } catch (error) {
      showError(error.response?.data?.message || "Não foi possível resolver os registros selecionados.");
    } finally {
      setResolvendoLote(false);
    }
  };

  const abrirCorrecao = (registro) => {
    let campos = {};
    try { campos = JSON.parse(registro.conteudoOriginalJson || "{}"); } catch { campos = {}; }
    setCorrigindo({ registro, destino: registro.destino, operacao: registro.operacao, campos });
  };

  const salvarCorrecao = async () => {
    if (!corrigindo) return;
    try {
      setSalvandoCorrecao(true);
      await api.post(
        `/importacoes-dados/${detalhe.importacao.id}/registros/${corrigindo.registro.id}/resolver`,
        {
          acao: "CORRIGIR",
          destino: corrigindo.destino,
          operacao: corrigindo.operacao,
          interpretacaoJson: JSON.stringify(corrigindo.campos),
        },
      );
      setCorrigindo(null);
      await abrir(detalhe.importacao.id);
      await carregar();
      showSuccess("Correção salva e lançamento processado novamente.");
    } catch (error) {
      showError(error.response?.data?.message || "Não foi possível aplicar a correção.");
    } finally { setSalvandoCorrecao(false); }
  };

  const renomearCampoCorrecao = (chaveAtual, novaChave) => setCorrigindo((atual) => {
    const campos = {};
    Object.entries(atual.campos).forEach(([chave, valor]) => { campos[chave === chaveAtual ? novaChave : chave] = valor; });
    return { ...atual, campos };
  });

  const adicionarCampoCorrecao = () => setCorrigindo((atual) => {
    let indice = 1;
    let chave = "Nova informação";
    while (Object.prototype.hasOwnProperty.call(atual.campos, chave)) chave = `Nova informação ${++indice}`;
    return { ...atual, campos: { ...atual.campos, [chave]: "" } };
  });

  const reprocessar = async (id) => {
    try {
      await api.post(`/importacoes-dados/${id}/reprocessar`);
      showSuccess("Reprocessamento colocado na fila.");
      await carregar();
    } catch (error) {
      showError(
        error.response?.data?.message || "Não foi possível reprocessar.",
      );
    }
  };

  const baixarRelatorio = async () => {
    try {
      const { data } = await api.get(
        `/importacoes-dados/${detalhe.importacao.id}/relatorio.csv`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `relatorio-importacao-${detalhe.importacao.id}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      showError("Não foi possível baixar o relatório.");
    }
  };

  const registros = (detalhe?.registros || []).filter(
    (item) => filtro === "TODOS" || item.status === filtro,
  );
  const pendentesFiltrados = registros.filter((item) => item.status === "PENDENTE");
  const totalErros = (detalhe?.registros || []).filter((item) => item.status === "ERRO").length;
  const totalAguardandoExecucao = (detalhe?.registros || []).filter((item) => item.status === "APROVADO").length;
  const totalPaginas = Math.max(1, Math.ceil(registros.length / ITENS_POR_PAGINA));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const registrosPagina = registros.slice((paginaAtual - 1) * ITENS_POR_PAGINA, paginaAtual * ITENS_POR_PAGINA);
  const selecionarTodos = () => setSelecionados(new Set(pendentesFiltrados.map((item) => item.id)));
  const alternarSelecao = (id) => setSelecionados((atuais) => {
    const proximos = new Set(atuais);
    if (proximos.has(id)) proximos.delete(id); else proximos.add(id);
    return proximos;
  });

  return (
    <>
      <Header />
      <main className="smart-import-page">
        <header className="smart-import-hero">
          <div>
            <span>IMPORTAÇÃO INTELIGENTE</span>
            <h1>Automação de dados agropecuários</h1>
            <p>
              Envie planilhas do produtor. O Cultiva+ identifica, valida e
              separa somente o que pode ser lançado com segurança.
            </p>
          </div>
          <i className="bi bi-file-earmark-spreadsheet" />
        </header>

        <section className="smart-import-metrics">
          <article>
            <i className="bi bi-files" />
            <div>
              <strong>{metricas?.arquivos || 0}</strong>
              <span>Arquivos</span>
            </div>
          </article>
          <article>
            <i className="bi bi-list-check" />
            <div>
              <strong>{metricas?.registros || 0}</strong>
              <span>Registros analisados</span>
            </div>
          </article>
          <article>
            <i className="bi bi-lightning-charge" />
            <div>
              <strong>{metricas?.percentualAutomacao || 0}%</strong>
              <span>Processamento concluído</span>
            </div>
          </article>
          <article>
            <i className="bi bi-shield-check" />
            <div>
              <strong>{metricas?.duplicidadesEvitadas || 0}</strong>
              <span>Duplicidades evitadas</span>
            </div>
          </article>
        </section>

        <section className="smart-import-upload">
          <div
            className={`smart-dropzone ${arrastando ? "dragging" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setArrastando(true);
            }}
            onDragLeave={() => setArrastando(false)}
            onDrop={(e) => {
              e.preventDefault();
              setArrastando(false);
              enviar(e.dataTransfer.files);
            }}
          >
            <i className="bi bi-cloud-arrow-up" />
            <h2>
              {enviando ? "Enviando arquivos..." : "Arraste suas planilhas aqui"}
            </h2>
            <p>
              Excel (.xlsx) ou CSV, com até 25 MB. Fórmulas e células são
              tratadas como dados não confiáveis.
            </p>
            <button
              type="button"
              disabled={enviando}
              onClick={() => inputRef.current?.click()}
            >
              <i className="bi bi-folder2-open" /> Selecionar arquivo
            </button>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.csv"
              multiple
              hidden
              onChange={(e) => enviar(e.target.files)}
            />
          </div>
          <aside>
            <strong>
              <i className="bi bi-shield-lock" /> Importação protegida
            </strong>
            <p>
              Nenhum lançamento ambíguo, fiscal ou sensível é gravado
              automaticamente. Você confirma apenas o que realmente precisar.
            </p>
          </aside>
        </section>

        <section className="smart-import-history">
          <div className="smart-section-title">
            <div>
              <span>HISTÓRICO</span>
              <h2>Importações recentes</h2>
            </div>
            <button type="button" onClick={carregar}>
              <i className="bi bi-arrow-clockwise" /> Atualizar
            </button>
          </div>
          <div className="smart-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Arquivo</th>
                  <th>Data</th>
                  <th>Status</th>
                  <th>Progresso</th>
                  <th>Registros</th>
                  <th>Pendências</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.id}>
                    <td data-label="Arquivo">
                      <strong>{job.nomeArquivo}</strong>
                    </td>
                    <td data-label="Data">{formatDate(job.criadoEmUtc)}</td>
                    <td data-label="Status">
                      <span
                        className={`smart-status status-${job.status.toLowerCase()}`}
                      >
                        {statusLabel[job.status] || job.status}
                      </span>
                    </td>
                    <td data-label="Progresso">
                      <div className="smart-progress">
                        <span style={{ width: `${job.progresso}%` }} />
                      </div>
                      <small>{job.progresso}%</small>
                    </td>
                    <td data-label="Registros">{job.totalRegistros}</td>
                    <td data-label="Pendências">
                      {job.totalPendentes}
                    </td>
                    <td>
                      <button
                        className="smart-view"
                        type="button"
                        onClick={() => abrir(job.id)}
                      >
                        Analisar <i className="bi bi-arrow-right" />
                      </button>
                    </td>
                  </tr>
                ))}
                {!jobs.length && (
                  <tr>
                    <td colSpan="7" className="smart-empty">
                      Nenhuma importação realizada.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {detalhe && (
          <div
            className="smart-analysis-overlay"
            role="dialog"
            aria-modal="true"
          >
            <section className="smart-analysis">
              <header>
                <div>
                  <span>ANÁLISE DA IMPORTAÇÃO</span>
                  <h2>{detalhe.importacao.nomeArquivo}</h2>
                  <p>
                    {detalhe.abas.length} aba(s) · {detalhe.destinos.length}{" "}
                    destino(s) identificado(s)
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Fechar"
                  onClick={() => setDetalhe(null)}
                >
                  <i className="bi bi-x-lg" />
                </button>
              </header>
              <div className="smart-analysis-summary">
                <span>
                  <strong>{detalhe.importacao.totalRegistros}</strong> registros
                </span>
                <span>
                  <strong>
                    {detalhe.importacao.totalPendentes}
                  </strong>{" "}
                  aguardando
                </span>
                <span>
                  <strong>{detalhe.importacao.totalDuplicados}</strong>{" "}
                  duplicados
                </span>
                <button type="button" onClick={baixarRelatorio}>
                  <i className="bi bi-download" /> Relatório CSV
                </button>
              </div>
              <nav className="smart-analysis-filters">
                {[
                  "TODOS",
                  "PENDENTE",
                  "APROVADO",
                  "REJEITADO",
                  "DUPLICADO",
                  "EXECUTADO",
                  "ERRO",
                ].map((item) => (
                  <button
                    type="button"
                    className={filtro === item ? "active" : ""}
                    onClick={() => { setFiltro(item); setPagina(1); setSelecionados(new Set()); }}
                    key={item}
                  >
                    {item === "TODOS" ? "Todos" : statusLabel[item]}
                  </button>
                ))}
              </nav>
              {detalhe.importacao.totalPendentes > 0 ? <div className="smart-bulk-toolbar">
                <div>
                  <label>
                    <input
                      type="checkbox"
                      checked={pendentesFiltrados.length > 0 && selecionados.size === pendentesFiltrados.length}
                      onChange={(event) => event.target.checked ? selecionarTodos() : setSelecionados(new Set())}
                    />
                    Selecionar todos os pendentes ({pendentesFiltrados.length})
                  </label>
                  <span>{selecionados.size} selecionado(s)</span>
                </div>
                <div>
                  <button type="button" disabled={!selecionados.size || resolvendoLote} onClick={() => resolverLote("IGNORAR")}>Ignorar selecionados</button>
                  <button type="button" className="confirm" disabled={!selecionados.size || resolvendoLote} onClick={() => resolverLote("CONFIRMAR")}>
                    <i className="bi bi-check2-all" /> Confirmar selecionados
                  </button>
                </div>
              </div> : totalAguardandoExecucao > 0 ? <div className="smart-complete-banner"><i className="bi bi-arrow-repeat" /><div><strong>Realizando lançamentos</strong><span>{totalAguardandoExecucao} registro(s) confirmado(s) ainda aguardam execução.</span></div></div>
                : totalErros > 0 ? <div className="smart-error"><span><strong>Importação concluída com pendências.</strong> {totalErros} registro(s) não foram lançados. Abra o filtro “Erro” para ver o motivo.</span></div>
                : <div className="smart-complete-banner"><i className="bi bi-check-circle-fill" /><div><strong>Lançamentos concluídos</strong><span>{detalhe.importacao.totalExecutados} registro(s) foram gravados nos módulos do sistema.</span></div></div>}
              <div className="smart-records">
                {registrosPagina.map((registro) => (
                  <article key={registro.id} className={selecionados.has(registro.id) ? "selected" : ""}>
                    <div className="smart-record-head">
                      {registro.status === "PENDENTE" && <input type="checkbox" aria-label={`Selecionar linha ${registro.linha}`} checked={selecionados.has(registro.id)} onChange={() => alternarSelecao(registro.id)} />}
                      <span
                        className={`smart-status status-${registro.status.toLowerCase()}`}
                      >
                        {statusLabel[registro.status] || registro.status}
                      </span>
                      <strong>
                        {registro.aba} · linha {registro.linha}
                      </strong>
                      <em>
                        {Math.round(registro.confianca * 100)}% de evidência
                      </em>
                    </div>
                    <div className="smart-record-body">
                      <div>
                        <small>Destino proposto</small>
                        <h3>
                          {registro.destino} / {registro.operacao}
                        </h3>
                        <p>{registro.motivo}</p>
                        {registro.erroExecucao && <p className="smart-record-error"><strong>Falha no lançamento:</strong> {registro.erroExecucao}</p>}
                      </div>
                      <details>
                        <summary>Ver dados originais</summary>
                        <pre>
                          {JSON.stringify(
                            JSON.parse(registro.conteudoOriginalJson || "{}"),
                            null,
                            2,
                          )}
                        </pre>
                      </details>
                    </div>
                    {registro.status === "PENDENTE" && (
                      <footer>
                        <button
                          type="button"
                          className="ignore"
                          onClick={() => resolver(registro, "IGNORAR")}
                        >
                          Ignorar
                        </button>
                        <button
                          type="button"
                          className="confirm"
                          onClick={() => resolver(registro, "CONFIRMAR")}
                        >
                          <i className="bi bi-check2" /> Confirmar interpretação
                        </button>
                      </footer>
                    )}
                    {registro.status === "ERRO" && (
                      <footer>
                        <button
                          type="button"
                          className="confirm"
                          onClick={() => abrirCorrecao(registro)}
                        >
                          <i className="bi bi-arrow-clockwise" /> Corrigir e tentar novamente
                        </button>
                      </footer>
                    )}
                  </article>
                ))}
                {!registros.length && (
                  <div className="smart-empty">
                    Nenhum registro neste filtro.
                  </div>
                )}
              </div>
              {registros.length > ITENS_POR_PAGINA && <div className="smart-pagination">
                <span>Exibindo {(paginaAtual - 1) * ITENS_POR_PAGINA + 1}–{Math.min(paginaAtual * ITENS_POR_PAGINA, registros.length)} de {registros.length}</span>
                <div>
                  <button type="button" disabled={paginaAtual === 1} onClick={() => setPagina((valor) => Math.max(1, valor - 1))}><i className="bi bi-chevron-left" /> Anterior</button>
                  <strong>Página {paginaAtual} de {totalPaginas}</strong>
                  <button type="button" disabled={paginaAtual === totalPaginas} onClick={() => setPagina((valor) => Math.min(totalPaginas, valor + 1))}>Próxima <i className="bi bi-chevron-right" /></button>
                </div>
              </div>}
              {detalhe.importacao.status === "FALHOU" && (
                <div className="smart-error">
                  <span>{detalhe.importacao.erro}</span>
                  <button
                    type="button"
                    onClick={() => reprocessar(detalhe.importacao.id)}
                  >
                    Reprocessar
                  </button>
                </div>
              )}
            </section>
            {corrigindo && <section className="smart-correction" role="dialog" aria-modal="true">
              <header>
                <div><span>CORRIGIR REGISTRO</span><h2>{corrigindo.registro.aba} · linha {corrigindo.registro.linha}</h2></div>
                <button type="button" aria-label="Fechar" onClick={() => setCorrigindo(null)}><i className="bi bi-x-lg" /></button>
              </header>
              <p className="smart-correction-help">Ajuste somente os dados incorretos ou ausentes. Ao salvar, o sistema tentará cadastrar os vínculos e realizar o lançamento novamente.</p>
              <div className="smart-correction-route">
                <label>Destino<select value={corrigindo.destino} onChange={(e) => setCorrigindo((x) => ({ ...x, destino: e.target.value }))}>
                  {["CADASTROS", "PRODUCAO", "APLICACOES", "ESTOQUE", "MAQUINAS", "LOGISTICA", "COMERCIAL", "FINANCEIRO", "PECUARIA", "NAO_IDENTIFICADO"].map((x) => <option key={x}>{x}</option>)}
                </select></label>
                <label>Operação<input value={corrigindo.operacao} onChange={(e) => setCorrigindo((x) => ({ ...x, operacao: e.target.value.toUpperCase() }))} /></label>
              </div>
              <div className="smart-correction-fields">
                {Object.entries(corrigindo.campos).map(([chave, valor]) => <label className="smart-correction-field" key={chave}>
                  <input aria-label="Nome do campo" className="field-name" value={chave} onChange={(e) => renomearCampoCorrecao(chave, e.target.value)} />
                  <input aria-label={`Valor de ${chave}`} value={valor ?? ""} onChange={(e) => setCorrigindo((x) => ({ ...x, campos: { ...x.campos, [chave]: e.target.value } }))} />
                </label>)}
              </div>
              <footer><button type="button" onClick={adicionarCampoCorrecao}><i className="bi bi-plus-lg" /> Adicionar informação</button><button type="button" onClick={() => setCorrigindo(null)}>Cancelar</button><button type="button" className="confirm" disabled={salvandoCorrecao} onClick={salvarCorrecao}><i className="bi bi-check2-circle" /> {salvandoCorrecao ? "Processando..." : "Salvar e tentar novamente"}</button></footer>
            </section>}
          </div>
        )}
      </main>
    </>
  );
}
